"use client"

import * as React from "react"
import { createPortal } from "react-dom"
import Link from "next/link"
import { ChevronDown, Menu, X } from "lucide-react"
import type { Gallery } from "@/lib/photos"
import { getSportsNavItems, getTravelNavItems } from "@/lib/gallery-navigation"
import { cn } from "@/lib/utils"

interface NavMenuProps {
  galleries: Gallery[]
  links?: Array<{
    label: string
    href: string
    visible: boolean
  }>
}

export function NavMenu({ galleries, links = [] }: NavMenuProps) {
  const [openMenu, setOpenMenu] = React.useState<'sports' | 'travel' | null>(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)
  const visibleLinks = links.filter(link => link.visible)
  const travelLink = visibleLinks.find(link => isTravelLink(link))
  const linksWithoutTravel = visibleLinks.filter(link => !isTravelLink(link))
  const travelHref = travelLink?.href || '/travel'

  // Close mobile menu when resizing to desktop
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Prevent scrolling when mobile menu is open
  React.useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => { document.body.style.overflow = 'unset' }
  }, [isMobileMenuOpen])

  const sportsNav = React.useMemo(() => getSportsNavItems(galleries), [galleries])
  const travelNav = React.useMemo(() => getTravelNavItems(galleries), [galleries])

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden md:flex items-center gap-6">
        <Link
          href="/"
          prefetch={false}
          className="font-heading tracking-wide text-xl text-slate-700 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white block rounded-md border-slate-200 px-4 py-2 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-50"
        >
          Home
        </Link>

        <DesktopDropdown
          label="Sports"
          menuKey="sports"
          isOpen={openMenu === 'sports'}
          onOpenChange={setOpenMenu}
          emptyLabel="No sports found"
          items={sportsNav.items.map((sport) => ({
            label: sport.name,
            href: `/?sport=${encodeURIComponent(sport.name)}`,
            count: sport.count,
            children: sport.years.map((year) => ({
              label: year.name,
              href: `/?sport=${encodeURIComponent(sport.name)}&year=${encodeURIComponent(year.name)}`,
              count: year.count,
            })),
          }))}
        />

        {travelLink && (
          <DesktopDropdown
            label={travelLink.label}
            menuKey="travel"
            isOpen={openMenu === 'travel'}
            onOpenChange={setOpenMenu}
            emptyLabel="No travel albums found"
            allItem={{ label: 'All travel albums', href: travelHref, count: travelNav.total }}
            items={travelNav.items.map((collection) => ({
              label: collection.name,
              href: collection.directGalleryId
                ? `/gallery/${collection.directGalleryId}`
                : getCollectionHref(travelHref, collection.collectionName),
              count: collection.count,
            }))}
          />
        )}

        {linksWithoutTravel.map((link) => (
          <Link
            key={`${link.label}-${link.href}`}
            href={link.href}
            prefetch={false}
            className="font-heading tracking-wide text-xl text-slate-700 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white block rounded-md border-slate-200 px-4 py-2 hover:bg-slate-100 hover:text-slate-900 dark:border-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-50"
          >
            {link.label}
          </Link>
        ))}
      </nav>

      {/* Mobile Navigation Toggle */}
      <button
        className="md:hidden p-2 text-slate-700 dark:text-slate-200"
        onClick={() => setIsMobileMenuOpen(true)}
        aria-label="Open menu"
      >
        <Menu className="h-6 w-6" />
      </button>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && createPortal(
        <div className="fixed inset-0 z-[100] md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Drawer */}
          <div className="absolute right-0 top-0 bottom-0 w-3/4 max-w-sm bg-background border-l border-border shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="font-heading text-xl tracking-wide">Menu</span>
              <button
                className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-accent"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex flex-col gap-4">
                <Link
                  href="/"
                  prefetch={false}
                  className="text-lg font-medium hover:text-primary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </Link>

                <div className="flex flex-col gap-2">
                  <div className="text-lg font-medium text-muted-foreground">
                    Sports
                  </div>
                  <div className="pl-4 flex flex-col gap-2">
                    {sportsNav.items.map((sport) => (
                      <div key={sport.name} className="grid gap-1">
                        <Link
                          href={`/?sport=${encodeURIComponent(sport.name)}`}
                          prefetch={false}
                          className="flex items-center justify-between text-base hover:text-primary transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <span>{sport.name}</span>
                          <span className="text-xs text-muted-foreground">{sport.count}</span>
                        </Link>
                        <div className="grid gap-1 pl-4">
                          {sport.years.map((year) => (
                            <Link
                              key={`${sport.name}-${year.name}`}
                              href={`/?sport=${encodeURIComponent(sport.name)}&year=${encodeURIComponent(year.name)}`}
                              prefetch={false}
                              className="flex items-center justify-between text-sm text-muted-foreground hover:text-primary transition-colors"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              <span>{year.name}</span>
                              <span className="text-xs">{year.count}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {travelLink && (
                  <div className="flex flex-col gap-2">
                    <div className="text-lg font-medium text-muted-foreground">
                      {travelLink.label}
                    </div>
                    <div className="pl-4 flex flex-col gap-2">
                      <Link
                        href={travelHref}
                        prefetch={false}
                        className="flex items-center justify-between text-base font-medium hover:text-primary transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <span>All travel albums</span>
                        <span className="text-xs text-muted-foreground">{travelNav.total}</span>
                      </Link>
                      {travelNav.items.map((collection) => (
                        <Link
                          key={collection.name}
                          href={collection.directGalleryId
                            ? `/gallery/${collection.directGalleryId}`
                            : getCollectionHref(travelHref, collection.collectionName)}
                          prefetch={false}
                          className="flex items-center justify-between text-base hover:text-primary transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <span>{collection.name}</span>
                          <span className="text-xs text-muted-foreground">{collection.count}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {linksWithoutTravel.map((link) => (
                  <Link
                    key={`${link.label}-${link.href}`}
                    href={link.href}
                    prefetch={false}
                    className="text-lg font-medium hover:text-primary transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

function isTravelLink(link: { label: string; href: string }) {
  const href = link.href.split('?')[0].replace(/\/$/, '').toLowerCase()
  return href === '/travel' || link.label.trim().toLowerCase() === 'travel'
}

function getCollectionHref(baseHref: string, collection: string) {
  const separator = baseHref.includes('?') ? '&' : '?'
  return `${baseHref}${separator}collection=${encodeURIComponent(collection)}`
}

function DesktopDropdown({
  label,
  menuKey,
  isOpen,
  onOpenChange,
  allItem,
  items,
  emptyLabel,
}: {
  label: string
  menuKey: 'sports' | 'travel'
  isOpen: boolean
  onOpenChange: (value: 'sports' | 'travel' | null) => void
  allItem?: { label: string; href: string; count: number }
  items: Array<{
    label: string
    href: string
    count: number
    children?: Array<{ label: string; href: string; count: number }>
  }>
  emptyLabel: string
}) {
  const [expandedItem, setExpandedItem] = React.useState<string | null>(null)

  return (
    <div
      className="relative group"
      onMouseEnter={() => onOpenChange(menuKey)}
      onMouseLeave={() => onOpenChange(null)}
    >
      <button
        className="flex items-center gap-1 font-heading tracking-wide text-xl text-slate-700 hover:text-slate-900 focus:outline-none dark:text-slate-200 dark:hover:text-white block rounded-md border-slate-200 px-4 py-2 hover:bg-slate-100 hover:text-slate-900 dark:border-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-50"
        onClick={() => onOpenChange(isOpen ? null : menuKey)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        {label}
        <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", isOpen && "rotate-180")} />
      </button>

      <div
        className={cn(
          "absolute left-0 top-full mt-2 w-72 rounded-xl bg-popover text-popover-foreground p-2 shadow-lg z-50 border border-border transition-all duration-200",
          isOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"
        )}
      >
        {items.length === 0 ? (
          <div className="px-4 py-2 text-sm text-slate-500">{emptyLabel}</div>
        ) : (
          <div className="flex flex-col gap-1">
            {allItem && (
              <Link
                href={allItem.href}
                prefetch={false}
                className="flex items-center justify-between rounded-lg px-4 py-2 text-sm font-medium hover:bg-slate-100 focus:outline-none dark:hover:bg-slate-800"
                onClick={() => onOpenChange(null)}
              >
                <span>{allItem.label}</span>
                <span className="text-xs text-muted-foreground">{allItem.count}</span>
              </Link>
            )}
            {items.map((item) => (
              <div key={`${item.label}-${item.href}`}>
                {item.children && item.children.length > 0 && (
                  <>
                    <div
                      className={cn(
                        "flex w-full items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800",
                        expandedItem === item.label && "bg-slate-100 font-medium dark:bg-slate-800"
                      )}
                      onMouseEnter={() => setExpandedItem(item.label)}
                    >
                      <Link
                        href={item.href}
                        prefetch={false}
                        className="flex min-h-10 min-w-0 flex-1 items-center justify-between gap-3 rounded-l-lg px-4 py-2 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => onOpenChange(null)}
                      >
                        <span className="truncate">{item.label}</span>
                        <span className="ml-auto text-xs text-muted-foreground">{item.count}</span>
                      </Link>
                      <button
                        type="button"
                        className="flex min-h-10 w-10 shrink-0 items-center justify-center rounded-r-lg text-muted-foreground transition-colors hover:bg-slate-200/60 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:hover:bg-slate-700"
                        onClick={() => setExpandedItem(expandedItem === item.label ? null : item.label)}
                        aria-expanded={expandedItem === item.label}
                        aria-label={`Show ${item.label} years`}
                      >
                      <ChevronDown className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform duration-200", expandedItem === item.label && "rotate-180")} />
                      </button>
                    </div>
                    <div
                      className={cn(
                        "overflow-hidden transition-all duration-200 ease-in-out",
                        expandedItem === item.label ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
                      )}
                    >
                      <div className="grid gap-1 py-1 pl-4">
                      {item.children.map((child) => (
                        <Link
                          key={`${item.label}-${child.label}`}
                          href={child.href}
                          prefetch={false}
                          className="flex items-center justify-between rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-50"
                          onClick={() => onOpenChange(null)}
                        >
                          <span>{child.label}</span>
                          <span className="text-xs text-muted-foreground">{child.count}</span>
                        </Link>
                      ))}
                      </div>
                    </div>
                  </>
                )}
                {(!item.children || item.children.length === 0) && (
                  <Link
                    href={item.href}
                    prefetch={false}
                    className="flex items-center justify-between gap-3 rounded-lg px-4 py-2 text-sm hover:bg-slate-100 focus:outline-none dark:hover:bg-slate-800"
                    onClick={() => onOpenChange(null)}
                  >
                    <span>{item.label}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{item.count}</span>
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
